import React, { useState } from "react";
import { Mic2, Radio, Globe2, Coins, Zap, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

const PACKAGES = [
  {
    id: "ad_3min",
    title: "3-minute slot",
    price: 35,
    duration: "Up to 3 minutes",
    plays: "Plays every few songs for 7 days",
    badge: "Most popular",
  },
  {
    id: "ad_5min",
    title: "5-minute slot",
    price: 45,
    duration: "Up to 5 minutes",
    plays: "Plays every few songs for 7 days",
    badge: "Best value · 40% more airtime",
  },
];

export default function Advertise() {
  const [selected, setSelected] = useState("ad_3min");
  const [business, setBusiness] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const start = async () => {
    setErr("");
    if (!business.trim()) { setErr("Tell us your business name."); return; }
    if (!email.trim()) { setErr("We need your email to send the upload link."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ads/checkout", {
        package_id: selected,
        business_name: business.trim(),
        contact_email: email.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start checkout. Try again in a moment.");
      setBusy(false);
    }
  };

  return (
    <main className="max-w-5xl mx-auto px-5 py-16" data-testid="advertise-page">
      <div className="text-center">
        <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-emerald-500 flex items-center justify-center gap-2">
          <Mic2 className="w-3.5 h-3.5" /> Sponsor a slot
        </div>
        <h1 className="fl-display text-5xl sm:text-6xl font-extrabold tracking-tighter mt-4">
          Your business.<br className="hidden sm:inline" /> <span className="text-emerald-500">On the radio.</span>
        </h1>
        <p className="text-slate-400 max-w-2xl mx-auto mt-5">
          Worldwide listeners hear your ad between songs. Real play counts, no agencies, no contracts.
          Live within 24 hours.
        </p>
      </div>

      {/* Value props */}
      <div className="grid sm:grid-cols-3 gap-4 mt-10">
        <ValueProp icon={<Globe2 className="w-5 h-5 text-emerald-500" />} title="Worldwide reach" body="Heard on every Fliboe station in rotation." />
        <ValueProp icon={<Zap className="w-5 h-5 text-emerald-500" />} title="Live in 24 hours" body="Upload, we approve, you go live." />
        <ValueProp icon={<Coins className="w-5 h-5 text-emerald-500" />} title="No contracts" body="Pay once. No agency. No BS." />
      </div>

      {/* Packages */}
      <h2 className="fl-display text-2xl font-bold mt-14 mb-5">Pick your package</h2>
      <div className="grid md:grid-cols-2 gap-5">
        {PACKAGES.map((p) => (
          <button
            key={p.id}
            onClick={() => setSelected(p.id)}
            className={`fl-card p-6 text-left transition-all ${selected === p.id ? "ring-2 ring-emerald-500 bg-emerald-500/5" : "hover:bg-white/5"}`}
            data-testid={`advertise-pkg-${p.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-emerald-500">{p.badge}</div>
                <div className="fl-display text-2xl font-bold mt-2">{p.title}</div>
              </div>
              <div className="text-right">
                <div className="fl-display text-4xl font-extrabold tracking-tighter">${p.price}</div>
                <div className="text-xs text-slate-500">one-time</div>
              </div>
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-slate-300">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> {p.duration}</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> {p.plays}</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Live play counts in your dashboard</div>
            </div>
          </button>
        ))}
      </div>

      {/* Form */}
      <div className="fl-card p-6 mt-8">
        <h3 className="fl-display text-xl font-bold mb-4">Tell us where to send your upload link</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs fl-mono uppercase tracking-widest text-slate-400">Business name</label>
            <input
              type="text"
              value={business}
              onChange={(e) => setBusiness(e.target.value)}
              placeholder="Joe's Pizza · 7th Avenue"
              className="fl-input mt-1.5"
              data-testid="advertise-business"
            />
          </div>
          <div>
            <label className="text-xs fl-mono uppercase tracking-widest text-slate-400">Contact email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@business.com"
              className="fl-input mt-1.5"
              data-testid="advertise-email"
            />
          </div>
        </div>
        {err && (
          <div className="mt-3 flex items-start gap-2 text-sm text-red-400" data-testid="advertise-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {err}
          </div>
        )}
        <button
          onClick={start}
          disabled={busy}
          className="fl-btn mt-5 w-full sm:w-auto"
          data-testid="advertise-checkout"
        >
          <Radio className="w-4 h-4" />
          {busy ? "Opening Stripe…" : `Pay $${PACKAGES.find(p => p.id === selected)?.price} and continue →`}
        </button>
        <p className="text-xs text-slate-500 mt-3">
          After payment you'll be redirected to upload your MP3. We'll email the upload link to {email || "your inbox"} as backup.
        </p>
      </div>
    </main>
  );
}

function ValueProp({ icon, title, body }) {
  return (
    <div className="fl-card p-5">
      <div className="flex items-center gap-2">
        {icon}
        <div className="text-white font-semibold">{title}</div>
      </div>
      <div className="text-xs text-slate-400 mt-2 leading-relaxed">{body}</div>
    </div>
  );
}
